/**
 * Vertical audit timeline. Renders chronologically (newest at top), each
 * entry shows actor, event type, optional diff. Long sessions are
 * paginated client-side at 50 entries per page. On-system styling (skill).
 */

import React, { useMemo, useState } from 'react';
import type { CheckInAuditEntry } from '@techgitter/contracts';

import { Chip, toneFromMuiColor } from '../../chip';

const PAGE_SIZE = 50;

const EVENT_COLOR: Record<string, string> = {
  session_created: 'primary',
  session_cancelled: 'default',
  session_expired: 'default',
  session_recovered: 'info',
  draft_job_added: 'info',
  draft_job_removed: 'default',
  draft_job_updated: 'info',
  charges_updated: 'info',
  charges_locked: 'default',
  customer_details_updated: 'info',
  internal_notes_updated: 'info',
  terms_accepted: 'success',
  terms_email_sent: 'info',
  rs_customer_created: 'success',
  rs_ticket_created: 'success',
  rs_invoice_created: 'success',
  rs_line_items_created: 'success',
  payment_marked_paid: 'success',
  payment_overridden: 'warning',
  payment_failed: 'error',
  inspection_started: 'info',
  inspection_updated: 'info',
  inspection_completed: 'success',
  photo_uploaded: 'info',
  photo_removed: 'default',
  orchestration_step_succeeded: 'success',
  orchestration_step_failed: 'error',
  orchestration_retried: 'warning',
  check_in_completed: 'success',
};

const fmt = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export interface CheckInAuditTimelineProps {
  entries: CheckInAuditEntry[];
}

export const CheckInAuditTimeline: React.FC<CheckInAuditTimelineProps> = ({ entries }) => {
  const [shown, setShown] = useState(PAGE_SIZE);

  const ordered = useMemo(() => {
    return [...entries].sort((a, b) => (a.occurredAt > b.occurredAt ? -1 : a.occurredAt < b.occurredAt ? 1 : 0));
  }, [entries]);

  if (ordered.length === 0) {
    return <div className="ci-caption">No audit events yet.</div>;
  }

  const visible = ordered.slice(0, shown);
  const hasMore = shown < ordered.length;

  return (
    <div className="ci-timeline">
      {visible.map(entry => (
        <AuditRow key={entry.id} entry={entry} />
      ))}
      {hasMore && (
        <button type="button" className="ci-textbtn" onClick={() => setShown(s => s + PAGE_SIZE)}>
          Load older events ({ordered.length - shown} remaining)
        </button>
      )}
    </div>
  );
};

const AuditRow: React.FC<{ entry: CheckInAuditEntry }> = ({ entry }) => {
  const [showDiff, setShowDiff] = useState(false);
  const hasDiff = !!(entry.previousValue || entry.newValue);

  return (
    <div className="ci-timeline__entry">
      <div className="ci-timeline__meta">
        <Chip tone={toneFromMuiColor(EVENT_COLOR[entry.eventType] ?? 'default')}>{entry.eventType}</Chip>
        <span className="ci-timeline__time">{fmt(entry.occurredAt)}</span>
        <span className="ci-timeline__sep">·</span>
        <span className="ci-timeline__actor">
          {entry.actorType}
          {entry.actorId ? ` ${entry.actorId}` : ''}
        </span>
      </div>
      {entry.reason && <div className="ci-timeline__reason">{entry.reason}</div>}
      {hasDiff && (
        <div style={{ marginTop: 4 }}>
          <button type="button" className="ci-textbtn" onClick={() => setShowDiff(s => !s)} style={{ padding: '2px 0' }}>
            {showDiff ? 'Hide diff' : 'Show diff'}
          </button>
          {showDiff && (
            <div className="ci-timeline__diff">
              {entry.previousValue && (
                <div>
                  <strong>was:</strong> {entry.previousValue}
                </div>
              )}
              {entry.newValue && (
                <div>
                  <strong>now:</strong> {entry.newValue}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {entry.correlationId && (
        <div className="ci-caption ci-mono" style={{ marginTop: 4 }}>
          msg: {entry.correlationId}
        </div>
      )}
    </div>
  );
};
